import { useState } from 'react';
import { Eraser, Maximize2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  SOURCE_MAX_CHARS,
  SOURCE_STRUCTURE_THRESHOLD,
  sourceContentStatus,
} from '@/lib/attachments';
import type { CopyPolicy } from '@/lib/generate';

export interface FormatContentEditorProps {
  value: string;
  onChange: (value: string) => void;
  copyPolicy: CopyPolicy;
  onCopyPolicyChange: (policy: CopyPolicy) => void;
  disabled?: boolean;
  hasAttachments?: boolean;
}

/** Analyze pasted content to give quick feedback. Pure frontend, no API call. */
function analyzeContent(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length < 8) return null;

  const paragraphs = trimmed.split(/\n\s*\n/).filter((p) => p.trim().length > 0);
  const lines = trimmed.split('\n').filter((l) => l.trim().length > 0);
  const parts: string[] = [];

  // Paragraph / line count
  if (paragraphs.length > 1) {
    parts.push(`${paragraphs.length} paragraphs`);
  } else {
    parts.push(`${lines.length} line${lines.length === 1 ? '' : 's'}`);
  }

  // Simple content-type hinting via keyword matching
  const lower = trimmed.toLowerCase();
  if (/\$\d|%\s*off|discount|coupon|promo|sale|free shipping/i.test(lower)) {
    parts.push('Looks like a promotion');
  } else if (/new feature|update|changelog|release|v\d/i.test(lower)) {
    parts.push('Looks like a product update');
  } else if (/welcome|onboard|get started|sign up/i.test(lower)) {
    parts.push('Looks like a welcome/onboarding email');
  } else if (/newsletter|digest|weekly|roundup|recap/i.test(lower)) {
    parts.push('Looks like a newsletter');
  } else if (/event|webinar|register|rsvp|conference/i.test(lower)) {
    parts.push('Looks like an event invitation');
  }

  return parts.join(' · ');
}

export function FormatContentEditor({
  value,
  onChange,
  copyPolicy,
  onCopyPolicyChange,
  disabled = false,
  hasAttachments = false,
}: FormatContentEditorProps) {
  const [expanded, setExpanded] = useState(false);
  const len = value.length;
  const status = sourceContentStatus(len);
  const contentHint = analyzeContent(value);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-muted-foreground text-xs uppercase tracking-wide">
          Paste your content
        </Label>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-7 px-2 text-[11px]"
            disabled={disabled || !value}
            onClick={() => onChange('')}
          >
            <Eraser className="size-3 mr-1" />
            Clear
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-7 px-2 text-[11px]"
            disabled={disabled}
            onClick={() => setExpanded(true)}
          >
            <Maximize2 className="size-3 mr-1" />
            Expand
          </Button>
        </div>
      </div>

      <div className="border-border bg-card shadow-sm rounded-md border">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={16}
          placeholder="Paste the copy you already prepared. We will turn it into email-safe HTML."
          className="max-h-[min(420px,50vh)] min-h-[280px] resize-none border-0 shadow-none focus-visible:ring-0"
        />
        <div
          className={cn(
            'border-border bg-muted/30 flex items-center justify-between gap-3 border-t px-3 py-2 text-[11px]',
            status.tone === 'error' && 'text-destructive',
            status.tone === 'warn' && 'text-warn',
            status.tone === 'ok' && 'text-muted-foreground',
          )}
        >
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="truncate">
              {len.toLocaleString()} / {SOURCE_MAX_CHARS.toLocaleString()} chars
              {contentHint ? ` · ${contentHint}` : len < 8 ? ' · Paste at least 8 characters' : ''}
            </span>
            {len > SOURCE_STRUCTURE_THRESHOLD && len <= SOURCE_MAX_CHARS ? (
              <span className="text-muted-foreground">
                Long content — will be pre-structured before formatting (~5s extra).
              </span>
            ) : null}
            {hasAttachments ? (
              <span className="text-muted-foreground">
                AI will use pasted content plus reference materials.
              </span>
            ) : null}
          </div>
          <Select
            value={copyPolicy}
            onValueChange={(v) => onCopyPolicyChange(v as CopyPolicy)}
            disabled={disabled}
          >
            <SelectTrigger className="h-7 w-auto gap-1.5 border border-input bg-background px-2.5 text-[11px] font-medium text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground focus:ring-0 shrink-0 transition-colors">
              {/* Radix requires SelectValue to link accessibility and functionality, so we render it visually hidden */}
              <div className="sr-only">
                <SelectValue />
              </div>
              <Wand2 className={cn("size-3", copyPolicy !== 'preserve' ? "text-primary" : "text-muted-foreground")} />
              <span className="truncate">
                {copyPolicy === 'preserve'
                  ? 'Preserve copy'
                  : copyPolicy === 'polish'
                    ? 'AI polish'
                    : 'AI rewrite'}
              </span>
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="preserve">
                <div>
                  <div className="text-xs font-medium">Preserve original copy</div>
                  <div className="text-muted-foreground text-[10px]">Keep wording intact; only structure and visual treatment change.</div>
                </div>
              </SelectItem>
              <SelectItem value="polish">
                <div>
                  <div className="text-xs font-medium">Let AI polish copy</div>
                  <div className="text-muted-foreground text-[10px]">Improve headings, CTA clarity, and long paragraphs.</div>
                </div>
              </SelectItem>
              <SelectItem value="rewrite">
                <div>
                  <div className="text-xs font-medium">Full AI rewrite</div>
                  <div className="text-muted-foreground text-[10px]">Rewrite copy completely from source facts and references.</div>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-h-[90vh] max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit content</DialogTitle>
          </DialogHeader>
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="min-h-[50vh] font-mono text-sm"
          />
          <p className="text-muted-foreground text-xs">
            {len.toLocaleString()} characters · {status.label}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onChange('')}>
              Clear
            </Button>
            <Button type="button" onClick={() => setExpanded(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { sourceContentStatus };
